/**
 * CanvasToolbar Component
 * Floating bottom toolbar for interaction mode switching (Figma-style)
 */

import { EditorTool } from '../../../map-editor-shared/store/uiStore'
import styles from './CanvasToolbar.module.css'

interface CanvasToolbarProps {
  currentTool: EditorTool
  onToolChange: (tool: EditorTool) => void
  canGroup?: boolean
  isGroupSelected?: boolean
  onGroup?: () => void
  onUngroup?: () => void
  /** 조명 자동 그룹핑 트리거. 미제공 시 버튼 미노출. */
  onAutoGroupLights?: () => void
}

export function CanvasToolbar({
  currentTool,
  onToolChange,
  canGroup,
  isGroupSelected,
  onGroup,
  onUngroup,
  onAutoGroupLights,
}: CanvasToolbarProps) {
  const isHandMode = currentTool !== 'move'
  const isMoveMode = currentTool === 'move'

  return (
    <div className={styles.toolbar}>
      <button
        className={`${styles.toolButton} ${isHandMode ? styles.toolButtonActive : ''}`}
        onClick={() => onToolChange('pan')}
        title="Hand Tool (Pan)"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0" />
          <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2" />
          <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8" />
          <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
        </svg>
      </button>

      <button
        className={`${styles.toolButton} ${isMoveMode ? styles.toolButtonActive : ''}`}
        onClick={() => onToolChange('move')}
        title="Drag Select (Marquee)"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4h4" /><path d="M16 4h4v4" />
          <path d="M20 16v4h-4" /><path d="M4 20h4" />
          <path d="M4 8v-4h4" /><path d="M4 16v4" />
          <path d="M20 8v-4" /><path d="M16 20h-4" />
          <rect x="8" y="8" width="8" height="8" strokeDasharray="2 2" />
        </svg>
      </button>

      {/* Group/Ungroup buttons */}
      {canGroup && (
        <>
          <div className={styles.divider} />
          <button
            className={styles.toolButton}
            onClick={onGroup}
            title="Group (Shift+G)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <path d="M10 6h4" /><path d="M10 18h4" />
              <path d="M6 10v4" /><path d="M18 10v4" />
            </svg>
          </button>
        </>
      )}
      {/* 조명 자동 그룹핑 — 선택과 무관하게 항상 노출 (handler 가 있을 때만). */}
      {onAutoGroupLights && (
        <>
          <div className={styles.divider} />
          <button
            className={styles.toolButton}
            onClick={onAutoGroupLights}
            title="조명 자동 그룹핑"
            aria-label="조명 자동 그룹핑"
          >
            {/* 전구 + sparkle = 자동/마법 그룹핑 의미 */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6" />
              <path d="M10 22h4" />
              <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2Z" />
              <path d="M19 4l.5 1.5L21 6l-1.5.5L19 8l-.5-1.5L17 6l1.5-.5Z" fill="currentColor" />
            </svg>
          </button>
        </>
      )}
      {isGroupSelected && (
        <>
          <div className={styles.divider} />
          <button
            className={styles.toolButton}
            onClick={onUngroup}
            title="Ungroup (Ctrl+Shift+G)"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
            </svg>
          </button>
        </>
      )}
    </div>
  )
}
